import React, { useState, useRef, useEffect } from 'react';
import * as Mui from '@material-ui/core';
import * as MuiIcons from '@material-ui/icons';
import { Eraser } from 'mdi-material-ui';
import Paper from '@material-ui/core/Paper';
import Assinatura from '~/components/Assinatura';
import { useTheme } from '@material-ui/core';

import assinaturaService from '~/store/modules/assinatura/assinatura.service';
import toast from '~/services/toast';
import { ASSINATURA_MODAL_TIPO_OPERACAO, ASSINATURA_TIPO_DOCUMENTO } from '~/constants';

const ModalAssinatura = props => {
  const theme = useTheme();

  const [assinaturaReqLoading, setAssinaturaReqLoading] = useState(false);

  const [statusAssinatura, setStatusAssinatura] = useState({
      alterado: false,
      vazio: true,
      animando: false
  });

  const arbitraryButtonEnableDelayMs = 500

  const [arbitraryButtonEnableDelayElapsed, setArbitraryButtonEnableDelayElapsed] = useState(false);

  useEffect(() => {
    if (!props.open) {
      setArbitraryButtonEnableDelayElapsed(false);
      return;
    }
    const timer = setTimeout(() => {
      setArbitraryButtonEnableDelayElapsed(true);
    }, arbitraryButtonEnableDelayMs);
    return () => clearTimeout(timer);
  }, [props.open]);

  const assinaturaRef = useRef(null);

  const salvar = async (assinaturaModalTipoOperacao) => {
    if (assinaturaReqLoading) return;

    if (assinaturaModalTipoOperacao === ASSINATURA_MODAL_TIPO_OPERACAO.EDITAR) {
      const { isConfirmed } = await toast.confirm(
        'Deseja substituir essa assinatura?',
        'Certifique-se de que realmente deseja fazer isso, pois esta ação é irreversível.',
        {
          icon: 'question',
          confirmButtonText: 'Substituir',
          cancelButtonText: 'Cancelar'
        }
      );
      if (!isConfirmed) return;
    }

    setAssinaturaReqLoading(true);
    try {
      const dadosAssinatura = assinaturaRef.current?.alterarPenColorDadosAssinatura(
        assinaturaRef.current?.obterDados(),
        "#000000"
      );
      const { data } = await assinaturaService.salvarAssinaturaByDocumento({
        tipoDocumento: props.tipoDocumento,
        documentoId: props.documentoId,
        assinatura: JSON.stringify(dadosAssinatura),
      });
      assinaturaRef.current?.marcarComoSalvo();
      props.onClose({
          confirmado: true,
          novaAssinatura: data,
      });
      toast.success('Documento assinado com sucesso!');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao assinar documento.');
    } finally {
      setAssinaturaReqLoading(false);
    }
  };

  const excluir = async (assinaturaModalTipoOperacao) => {
    if (assinaturaReqLoading) return;

    if (assinaturaModalTipoOperacao === ASSINATURA_MODAL_TIPO_OPERACAO.REMOVER) {
      const { isConfirmed } = await toast.confirm(
        'Deseja remover essa assinatura?',
        'Certifique-se de que realmente deseja fazer isso, pois esta ação é irreversível.',
        {
          icon: 'question',
          confirmButtonText: 'Remover',
          cancelButtonText: 'Cancelar'
        }
      );
      if (!isConfirmed) return;
    }

    setAssinaturaReqLoading(true);
    try {
      const { data } = await assinaturaService.removerAssinaturaByDocumento({
        tipoDocumento: props.tipoDocumento,
        documentoId: props.documentoId,
      });
      props.onClose({
          confirmado: true,
          novaAssinatura: null,
      });
      toast.success('Assinatura removida com sucesso!');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao remover assinatura.');
    } finally {
      setAssinaturaReqLoading(false);
    }
  };

  const getTituloModal = (assinaturaModalTipoOperacao) => {
    if (assinaturaModalTipoOperacao == null) {
      return "Assinatura";
    }
    switch (assinaturaModalTipoOperacao) {
      case ASSINATURA_MODAL_TIPO_OPERACAO.VISUALIZAR:
        return "Assinatura do documento";
      case ASSINATURA_MODAL_TIPO_OPERACAO.ADICIONAR:
        return "Assinar documento";
      case ASSINATURA_MODAL_TIPO_OPERACAO.EDITAR:
        return "Alterar assinatura do documento";
      case ASSINATURA_MODAL_TIPO_OPERACAO.REMOVER:
        return "Remover assinatura do documento";
    }
  };

  const getDescricaoVinculo = (assinaturaModalTipoOperacao, tipoDocumento) => {
    if (assinaturaModalTipoOperacao == null || tipoDocumento == null) {
      return "";
    }
    const tipoDocFormatado = getTipoDocumento(tipoDocumento).toLowerCase();
    switch (assinaturaModalTipoOperacao) {
      case ASSINATURA_MODAL_TIPO_OPERACAO.VISUALIZAR:
        return `A assinatura está vinculada ao ${tipoDocFormatado}.`;
      case ASSINATURA_MODAL_TIPO_OPERACAO.ADICIONAR:
        return `A assinatura será vinculada ao ${tipoDocFormatado}.`;
      case ASSINATURA_MODAL_TIPO_OPERACAO.EDITAR:
        return `A nova assinatura será vinculada ao ${tipoDocFormatado}.`;
      case ASSINATURA_MODAL_TIPO_OPERACAO.REMOVER:
        return `A assinatura vinculada será removida do ${tipoDocFormatado}.`;
    }
  };

  const getTipoDocumento = (tipoDocumento) => {
    if (tipoDocumento == null) {
      return "Documento";
    }
    switch (tipoDocumento) {
      case ASSINATURA_TIPO_DOCUMENTO.ORCAMENTO:
        return "Orçamento";
      case ASSINATURA_TIPO_DOCUMENTO.PEDIDO:
        return "Pedido";
      case ASSINATURA_TIPO_DOCUMENTO.DOCUMENTOSAIDA:
        return "Documento de saída";
    }
  };

  return (
    <>
      <Mui.Dialog
        open={props.open}
        TransitionProps={{
            onEntering: () => {
                assinaturaRef.current?.inicializar();
                assinaturaRef.current?.definirCor(theme.palette.text.primary)
                if (!props.assinaturaSalva) {
                    return;
                }
                const dadosAssinatura = assinaturaRef.current?.alterarPenColorDadosAssinatura(props.assinaturaSalva, theme.palette.text.primary)
                if (
                  props.assinaturaModalTipoOperacao === ASSINATURA_MODAL_TIPO_OPERACAO.VISUALIZAR ||
                  props.assinaturaModalTipoOperacao === ASSINATURA_MODAL_TIPO_OPERACAO.REMOVER
                ) {
                  assinaturaRef.current?.desabilitar();
                  assinaturaRef.current?.definirDados(dadosAssinatura);
                }
            },
            onEntered: () => {
                if (props.assinaturaSalva) {
                    // Animação de abertura do modal finalizada.
                    return;
                }
            }
        }}
        onKeyDown={key => {
          if (key.code === 'Escape') {
            if (!assinaturaReqLoading) {
              props.onClose({
                  confirmado: false,
              });
            }
          }
        }}
        PaperComponent={Paper}
        PaperProps={{
          style: {
            width: '70%',
            maxWidth: '70%',
            maxHeight: '100%',
          },
        }}
        fullWidth={true}
      >

        <Mui.DialogTitle>
          <Mui.AppBar color="primary" position="static">
            <Mui.Toolbar>
              <Mui.Box style={{ display: 'flex', minWidth: 180, alignItems: 'center', flexDirection: 'row', gap: '16px' }}>
                <Mui.Typography
                  variant="h5"
                  style={{ textAlign: 'center', textTransform: 'uppercase', fontSize: 18, fontWeight: 600 }}
                >
                  {getTituloModal(props.assinaturaModalTipoOperacao)}
                </Mui.Typography>
                <Mui.Chip
                  label={`${getTipoDocumento(props.tipoDocumento)} nº ${props.numeroDocumento}`}
                  size='small'
                  style={{ backgroundColor: theme.palette.primary.mainLightBg, color: theme.palette.primary.main, fontWeight: 600, borderRadius: 9999 }}
                />
              </Mui.Box>
              <Mui.Box flexGrow={1} />
              <Mui.IconButton color="inherit" onClick={() => props.onClose({
                  confirmado: false,
              })} aria-label="close">
                <MuiIcons.Close />
              </Mui.IconButton>
            </Mui.Toolbar>
          </Mui.AppBar>
        </Mui.DialogTitle>

        <Mui.DialogContent dividers style={{ padding: '32px 24px 24px 24px' }}>

          {(props.assinaturaModalTipoOperacao == ASSINATURA_MODAL_TIPO_OPERACAO.EDITAR || props.assinaturaModalTipoOperacao == ASSINATURA_MODAL_TIPO_OPERACAO.ADICIONAR) &&
            <Mui.Typography color="textSecondary" style={{marginBottom: '16px'}}>Desenhe sua assinatura no campo abaixo.</Mui.Typography>
          }

          <Assinatura
              ref={assinaturaRef}
              altura={320}
              placeholder="Use o mouse ou toque na tela para assinar"
              mostrarPlaceholder={props.somenteLeitura ? false : true}
              somenteLeitura={props.somenteLeitura}
              onStatusChange={(status) => {
                setStatusAssinatura(status)
              }}
          />

          <Mui.Box display="flex" style={{marginTop: '16px', gap: '8px', flexGrow: 1, flexShrink: 1, flexWrap: 'wrap', alignItems: 'center', flexDirection: 'row', justifyContent: (props.assinaturaModalTipoOperacao === ASSINATURA_MODAL_TIPO_OPERACAO.VISUALIZAR || props.assinaturaModalTipoOperacao === ASSINATURA_MODAL_TIPO_OPERACAO.REMOVER) ? 'space-between' : 'flex-start'}}>
              <Mui.Box display="flex" alignItems="center">
                <MuiIcons.VerifiedUserOutlined style={{color: theme.palette.success.main, marginRight: 8}} />
                <Mui.Typography variant="body2" color="textSecondary">
                  {getDescricaoVinculo(props.assinaturaModalTipoOperacao, props.tipoDocumento)}
                </Mui.Typography>
              </Mui.Box>
              {props.assinaturaModalTipoOperacao === ASSINATURA_MODAL_TIPO_OPERACAO.VISUALIZAR && (
                  <Mui.Box display="flex" style={{ gap: '8px', flexShrink: 1, flexWrap: 'wrap', alignItems: 'center', flexDirection: 'row'}}>
                      <Mui.Button
                          variant="contained"
                          color="primary"
                          onClick={() => props.onClose({
                              confirmado: false,
                          })}
                          disabled={!arbitraryButtonEnableDelayElapsed}
                      >
                          OK
                      </Mui.Button>
                  </Mui.Box>
              )}
              {props.assinaturaModalTipoOperacao === ASSINATURA_MODAL_TIPO_OPERACAO.REMOVER && (
                  <Mui.Box display="flex" style={{ gap: '8px', flexShrink: 1, flexWrap: 'wrap', alignItems: 'center', flexDirection: 'row'}}>
                      <Mui.Button
                          variant="outlined"
                          color="primary"
                          onClick={() => props.onClose({
                              confirmado: false,
                          })}
                      >
                          Cancelar
                      </Mui.Button>

                      <Mui.Button
                        variant="contained"
                        className='button-danger'
                        disabled={!arbitraryButtonEnableDelayElapsed || assinaturaReqLoading || statusAssinatura.animando}
                        onClick={() => excluir(props.assinaturaModalTipoOperacao)}
                      >
                        {assinaturaReqLoading ? "Carregando..." : "Excluir"}
                      </Mui.Button>
                  </Mui.Box>
              )}
          </Mui.Box>

          <Mui.DialogActions style={{padding: '0px'}}>
              {(
                props.assinaturaModalTipoOperacao !== ASSINATURA_MODAL_TIPO_OPERACAO.VISUALIZAR &&
                props.assinaturaModalTipoOperacao !== ASSINATURA_MODAL_TIPO_OPERACAO.REMOVER
              ) && (
                  <Mui.Box display="flex" style={{ marginTop: '16px', gap: '8px', flexGrow: 1, flexShrink: 1, flexWrap: 'wrap', alignItems: 'center', flexDirection: 'row'}}>
                      <Mui.Box display="flex" style={{ gap: '8px', flexShrink: 1, flexWrap: 'wrap', alignItems: 'center', flexDirection: 'row'}}>
                          <Mui.Button
                              variant="outlined"
                              startIcon={
                                <Eraser />
                              }
                              disabled={!arbitraryButtonEnableDelayElapsed || assinaturaReqLoading || statusAssinatura.animando}
                              onClick={() => assinaturaRef.current?.limpar()}
                          >
                            Limpar
                          </Mui.Button>
                      </Mui.Box>

                      <Mui.Box flex={1} />

                      <Mui.Box display="flex" style={{ gap: '8px', flexShrink: 1, flexWrap: 'wrap', alignItems: 'center', flexDirection: 'row'}}>
                          <Mui.Button
                              variant="outlined"
                              color="primary"
                              onClick={() => props.onClose({
                                  confirmado: false,
                              })}
                          >
                              Cancelar
                          </Mui.Button>

                          <Mui.Button
                            variant="contained"
                            className='button-success'
                            disabled={!arbitraryButtonEnableDelayElapsed || assinaturaReqLoading || statusAssinatura.animando || !statusAssinatura.alterado || statusAssinatura.vazio}
                            onClick={() => salvar(props.assinaturaModalTipoOperacao)}
                          >
                            {assinaturaReqLoading ? "Carregando..." : "Salvar"}
                          </Mui.Button>
                      </Mui.Box>
                  </Mui.Box>
              )}

          </Mui.DialogActions>
        </Mui.DialogContent>
      </Mui.Dialog>
    </>
  );
};

export default ModalAssinatura;
